/**
 * Installing AirPost as an app on a computer or a tablet (ADR 093): the browser's own install flow where it offers
 * one (Chrome and Edge fire `beforeinstallprompt`), else the words for the platform's own menu. Pure logic here;
 * the words are in the dialog.
 */
export type InstallWay = 'installed' | 'prompt' | 'ios' | 'android' | 'desktop';

export interface InstallEnv {
  ua: string;
  /** Already running as an installed app (standalone display mode). */
  standalone: boolean;
  /** `navigator.maxTouchPoints`: an iPad with a desktop user agent still has touch points. */
  touchPoints: number;
  /** The browser offered its install prompt and we hold it. */
  hasPrompt: boolean;
}

export function installWay(env: InstallEnv): InstallWay {
  if (env.standalone) return 'installed';
  if (env.hasPrompt) return 'prompt';
  if (/iPad|iPhone|iPod/.test(env.ua) || (/Macintosh/.test(env.ua) && env.touchPoints > 1)) return 'ios';
  if (/Android/.test(env.ua)) return 'android';
  return 'desktop';
}

export const isStandalone = (): boolean =>
  typeof window !== 'undefined' && (window.matchMedia?.('(display-mode: standalone)').matches === true || (navigator as { standalone?: boolean }).standalone === true);

type PromptEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }> };

let deferred: PromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as PromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    installed = true;
    notify();
  });
}

/** For `useSyncExternalStore`: changes whenever the prompt arrives, is used, or the app gets installed. */
export const subscribeInstall = (l: () => void): (() => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
export const installSnapshot = (): string => `${installed}|${deferred !== null}`;

export const currentInstallWay = (): InstallWay =>
  installWay({ ua: navigator.userAgent, standalone: installed || isStandalone(), touchPoints: navigator.maxTouchPoints ?? 0, hasPrompt: deferred !== null });

/** Show the browser's install prompt we hold; 'none' when there is none to show. */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'none'> {
  const e = deferred;
  if (!e) return 'none';
  await e.prompt();
  const { outcome } = await e.userChoice;
  deferred = null;
  notify();
  return outcome;
}

/** The service worker at the root (public/sw.js): network only, an offline page, and installability everywhere. */
export function registerServiceWorker(): void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator) || !window.isSecureContext) return;
  navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => undefined);
}
